"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { useTheme } from "next-themes";

interface CompanySettings {
  theme: "light" | "dark";
  primaryColor: string;
  logoUrl: string | null;
}

interface CompanyContextType {
  settings: CompanySettings;
  updateTheme: (theme: "light" | "dark") => void;
  updateColor: (color: string) => void;
  updateLogo: (logoUrl: string | null) => void;
  mounted: boolean;
}

const DEFAULT_PRIMARY_COLOR = "var(--company-default)";
const DEFAULT_LOGO = "/images/sua-logo2.png";

const DEFAULT_SETTINGS: CompanySettings = {
  theme: "light",
  primaryColor: DEFAULT_PRIMARY_COLOR,
  logoUrl: DEFAULT_LOGO,
};

// Valores padrão para quando o contexto não está disponível
const defaultContextValue: CompanyContextType = {
  settings: DEFAULT_SETTINGS,
  updateTheme: () => console.warn("CompanyProvider não encontrado"),
  updateColor: () => console.warn("CompanyProvider não encontrado"),
  updateLogo: () => console.warn("CompanyProvider não encontrado"),
  mounted: false,
};

const CompanyContext = createContext<CompanyContextType>(defaultContextValue);

export function CompanyProvider({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = useState(false);
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY_COLOR);
  const [logoUrl, setLogoUrl] = useState<string | null>(DEFAULT_LOGO);

  // Aplica a cor primária ao DOM
  const updateColor = (color: string) => {
    if (typeof window === "undefined") return;
    setPrimaryColor(color);
    document.documentElement.style.setProperty("--primary", color);
    document.documentElement.style.setProperty(
      "--primary-foreground",
      (resolvedTheme || theme) === "dark" ? "oklch(0.205 0 0)" : "oklch(0.985 0 0)"
    );
  };

  const updateTheme = (newTheme: "light" | "dark") => {
    setTheme(newTheme);
  };

  const updateLogo = (newLogoUrl: string | null) => {
    // Se a nova URL for null ou vazia, usar a logo padrão
    setLogoUrl(newLogoUrl || DEFAULT_LOGO);
    // Aqui você pode adicionar lógica para salvar a logo em localStorage ou API
  };

  // Atualiza a cor do foreground quando o tema mudar
  useEffect(() => {
    if (!mounted || typeof window === "undefined") return;

    document.documentElement.style.setProperty(
      "--primary-foreground",
      (resolvedTheme || theme) === "dark" ? "oklch(0.205 0 0)" : "oklch(0.985 0 0)"
    );
  }, [theme, resolvedTheme, mounted]);

  // Efeito para marcar quando o componente está montado
  useEffect(() => {
    setMounted(true);
    // Aplica a cor inicial
    updateColor(DEFAULT_PRIMARY_COLOR);
    // Aqui você pode carregar a logo salva do localStorage ou API
  }, []);

  const settings: CompanySettings = {
    theme: (theme as "light" | "dark") || "light",
    primaryColor,
    logoUrl,
  };

  return (
    <CompanyContext.Provider
      value={{
        settings,
        updateTheme,
        updateColor,
        updateLogo,
        mounted,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

/**
 * Hook para gerenciar as configurações de company
 * @returns {object} Objeto contendo as configurações de company e funções de atualização
 */
export function useCompany() {
  const context = useContext(CompanyContext);

  // Se não estiver montado, retorna os valores padrão com flag de carregamento
  if (!context.mounted) {
    return {
      settings: DEFAULT_SETTINGS,
      updateTheme: defaultContextValue.updateTheme,
      updateColor: defaultContextValue.updateColor,
      updateLogo: defaultContextValue.updateLogo,
      isLoading: true,
    };
  }

  return {
    ...context,
    isLoading: false,
  };
}
